import type { UtmKey, UtmValues } from '@/components/builder/UtmSection'
import type { Preset } from '@/hooks/useLinkBuilderDataEntry'
import {
  chainAncestors,
  chainDescendants,
  connectorOf,
  type PickerBinding,
} from '@/lib/pickerBindings'
import type { TagEntry } from '@/lib/tagEntry'

const KNOWN_UTM_KEYS: ReadonlySet<UtmKey> = new Set([
  'source',
  'medium',
  'campaign',
  'content',
  'term',
  'id',
  'source_platform',
  'creative_format',
  'marketing_tactic',
])

export interface AppliedPreset {
  utm: UtmValues
  tags: TagEntry[]
}

interface ApplyArgs {
  preset: Preset
  analyticsKeys: ReadonlySet<string>
  pickerBindings: ReadonlyMap<string, PickerBinding>
}

/**
 * Decomposes a preset's flat queryParameters into the builder's split shape.
 * - utm_<known-key> goes into UtmValues (suffix only).
 * - keys matching an analyticsField tagDefinition go in tags without rawKey.
 * - everything else goes in tags WITH rawKey set, so the Custom section
 *   renders it as an editable row (the original raw-typed input is not
 *   stored on the server, so we mirror the key itself).
 *
 * tagValue undefined is collapsed to '' here; toPresetQueryParameters does
 * the inverse, so a placeholder field round-trips correctly.
 *
 * A picked pair — a bound name key's label alongside its `_id` twin's value —
 * is re-absorbed into one picked entry, so applying renders the field exactly
 * as it was saved, stale ids included (a dangling id is classified by the
 * measures downstream, never checked here). A twin missing its half, or a
 * pair with either half blank, stays ordinary rows.
 */
export function applyPreset({ preset, analyticsKeys, pickerBindings }: ApplyArgs): AppliedPreset {
  const utm: UtmValues = {}
  const tags: TagEntry[] = []

  const rowByKey = new Map<string, string | undefined>()
  for (const qp of preset.queryParameters) {
    if (!rowByKey.has(qp.tagKey)) rowByKey.set(qp.tagKey, qp.tagValue)
  }

  const pickedValueByNameKey = new Map<string, string>()
  const absorbedTwinKeys = new Set<string>()
  for (const [nameKey, binding] of pickerBindings) {
    if (binding.idKey === null || !rowByKey.has(binding.idKey)) continue
    const label = rowByKey.get(nameKey)
    const idValue = rowByKey.get(binding.idKey)
    if (!label || !idValue) continue
    pickedValueByNameKey.set(nameKey, idValue)
    absorbedTwinKeys.add(binding.idKey)
  }

  // A twin-less head saves no id of its own, so read it back off a descendant's reference.
  for (const [nameKey, binding] of pickerBindings) {
    if (binding.idKey !== null || !rowByKey.get(nameKey)) continue
    for (const descendant of chainDescendants(nameKey, pickerBindings)) {
      const reference = pickedValueByNameKey.get(descendant)
      const connector = reference === undefined ? null : connectorOf(reference)
      if (connector !== null) {
        pickedValueByNameKey.set(nameKey, connector)
        break
      }
    }
  }

  for (const qp of preset.queryParameters) {
    if (qp.tagKey === '') continue
    if (absorbedTwinKeys.has(qp.tagKey)) continue
    const utmKey = matchKnownUtmKey(qp.tagKey)
    if (utmKey !== null) {
      utm[utmKey] = qp.tagValue ?? ''
      continue
    }
    const tag: TagEntry = {
      id: crypto.randomUUID(),
      key: qp.tagKey,
      value: qp.tagValue ?? '',
    }
    if (!analyticsKeys.has(qp.tagKey)) {
      tag.rawKey = qp.tagKey
    }
    const pickedValue = pickedValueByNameKey.get(qp.tagKey)
    if (pickedValue !== undefined) {
      tag.pickedValue = pickedValue
    }
    tags.push(tag)
  }

  // A saved child without its parent can never offer options, so bring the parent back with it.
  const present = new Set(tags.map((t) => t.key))
  for (const tag of [...tags]) {
    for (const ancestor of chainAncestors(tag.key, pickerBindings)) {
      if (present.has(ancestor)) continue
      present.add(ancestor)
      tags.push({ id: crypto.randomUUID(), key: ancestor, value: '' })
    }
  }

  return { utm, tags }
}

function matchKnownUtmKey(tagKey: string): UtmKey | null {
  if (!tagKey.startsWith('utm_')) return null
  const suffix = tagKey.slice(4)
  return KNOWN_UTM_KEYS.has(suffix as UtmKey) ? (suffix as UtmKey) : null
}
